import { useCallback, useContext, useState } from 'react';
import { sparkToast } from '@spark';
import { useNavigate } from 'react-router-dom';
import { data_from_server_post } from '@/shared/http/client';
import { GlobalContext } from '@/app/providers/globalState';
import { DEFAULT_CLIENT_ID } from '@/config/client';
import { deploy_in_progress, undeploy_in_progress } from '@/shared/constants/taskStatuses';
import {
    deletedTasksToastTitle,
    displayTaskName,
} from '@/features/tasks/utils/projectDeletedToast.jsx';
import {
    showBulkDeleteResultToast,
    showDeleteFailureToast,
} from '@/features/tasks/utils/taskDeleteToast';
import { runBulkMove } from '@/features/tasks/utils/bulkMoveTasks';

/**
 * Task-page counterpart of the homepage task handlers (`useHomePage`):
 * delete / bulk delete / deploy / duplicate / edit for the task currently
 * open on `/task/:taskName`. Same backend contracts, but scoped so the task
 * page doesn't drag in the homepage's filters and project modals, and
 * deleting the whole task navigates back to All Projects.
 *
 * The returned callbacks match the `useTaskActions` consumer contract
 * (onDeleteUsecase, onBulkDeleteUsecases, onModelDeployment,
 * onDuplicateUsecase, onEditTask).
 */
export const useTaskPageActions = () => {
    const navigate = useNavigate();
    const { allUsecases, refreshUsecases, updateStatus } = useContext(GlobalContext);

    const [isDuplicateTaskModalOpen, setIsDuplicateTaskModalOpen] = useState(false);
    const [taskToDuplicate, setTaskToDuplicate] = useState(null);
    const [isEditTaskModalOpen, setIsEditTaskModalOpen] = useState(false);
    const [taskToEdit, setTaskToEdit] = useState(null);
    const [editTaskMode, setEditTaskMode] = useState('umbrella');
    const [projectCreatedBar, setProjectCreatedBar] = useState({
        visible: false,
        projectName: '',
        taskName: '',
        usecaseId: null,
        isProjectCreation: false,
    });

    const handleDeleteUsecase = useCallback(async (client_id, usecase_id) => {
        try {
            const usecase = allUsecases.find((u) => u._id === usecase_id);
            const taskName = usecase?.usecase_name || displayTaskName(usecase);
            data_from_server_post(
                'delete_llm_usecase',
                { client_id, usecase_id },
                (result) => {
                    if (result.success) {
                        sparkToast.success(deletedTasksToastTitle([taskName].filter(Boolean)));
                        refreshUsecases();
                    } else {
                        showDeleteFailureToast(result.message, 'Failed to delete usecase');
                    }
                }
            );
        } catch (error) {
            sparkToast.error('Failed to delete usecase');
        }
    }, [allUsecases, refreshUsecases]);

    /** Deletes every objective of the task, then returns to All Projects. */
    const handleBulkDeleteUsecases = useCallback(async (usecases = []) => {
        try {
            if (!Array.isArray(usecases) || usecases.length === 0) return;

            const byClient = new Map();
            usecases.forEach((u) => {
                if (!u?.client_id || !u?._id) return;
                if (!byClient.has(u.client_id)) byClient.set(u.client_id, []);
                byClient.get(u.client_id).push(u._id);
            });

            const results = await Promise.all(
                Array.from(byClient.entries()).map(([client_id, ids]) =>
                    data_from_server_post('bulk-delete-usecases', {
                        client_id,
                        usecase_ids: ids,
                    })
                )
            );

            const deleted = results.reduce((acc, r) => acc + (r?.deleted_count || 0), 0);
            const failed = results.reduce((acc, r) => acc + (r?.failed_count || 0), 0);
            const failures = results.flatMap((r) => r?.failures || []);

            showBulkDeleteResultToast({ deleted, failed, failures, usecases });

            refreshUsecases();
            if (deleted > 0) {
                navigate('/');
            }
        } catch (error) {
            sparkToast.error('Failed to delete tasks');
        }
    }, [navigate, refreshUsecases]);

    const handleModelDeployment = useCallback(async (usecase_id, action, version = null) => {
        try {
            const usecase = allUsecases.find((u) => u._id === usecase_id);
            if (!usecase) {
                sparkToast.error('Usecase not found');
                return;
            }

            const statusUpdate = action === 'deploy' ? deploy_in_progress : undeploy_in_progress;
            updateStatus(usecase_id, statusUpdate);

            const deployed_version = action === 'deploy'
                ? (version !== null && version !== undefined ? Number(version) : usecase.version)
                : usecase.deployed_version;

            data_from_server_post(
                'model-deployment',
                {
                    client_id: usecase.client_id,
                    llm_usecase_id: usecase_id,
                    task_type: usecase.task_type,
                    request_type: action,
                    deployed_version,
                },
                (result) => {
                    if (result.success) {
                        sparkToast.success(`${action} in progress`);
                        setTimeout(refreshUsecases, 2000);
                    } else {
                        sparkToast.error(`Failed to ${action} model`);
                        refreshUsecases();
                    }
                }
            );
        } catch (error) {
            sparkToast.error(`Failed to ${action} model`);
            refreshUsecases();
        }
    }, [allUsecases, refreshUsecases, updateStatus]);

    const handleDuplicateUsecase = useCallback((originalTask) => {
        setTaskToDuplicate(originalTask);
        setIsDuplicateTaskModalOpen(true);
    }, []);

    const closeDuplicateTaskModal = useCallback(() => {
        setIsDuplicateTaskModalOpen(false);
        setTaskToDuplicate(null);
    }, []);

    const closeProjectCreatedBar = useCallback(() => {
        setProjectCreatedBar({
            visible: false,
            projectName: '',
            taskName: '',
            usecaseId: null,
            isProjectCreation: false,
        });
    }, []);

    const handleCreateDuplicateTask = useCallback((duplicatedTaskData) => {
        const duplicatedTasks = Array.isArray(duplicatedTaskData)
            ? duplicatedTaskData
            : [duplicatedTaskData];
        const firstDuplicatedTask = duplicatedTasks[0];
        const duplicatedTaskName =
            firstDuplicatedTask?.sub_group_name || firstDuplicatedTask?.usecase_name;

        if (!firstDuplicatedTask || !duplicatedTaskName) {
            sparkToast.error('No task selected for duplication');
            return;
        }

        data_from_server_post(
            'create_llm_usecase',
            {
                tasks: duplicatedTasks,
                group_name: firstDuplicatedTask.group_name,
                client_id: DEFAULT_CLIENT_ID,
            },
            (result) => {
                if (result.success) {
                    refreshUsecases((usecases) => {
                        const newUsecase = usecases.find(
                            (u) =>
                                u.sub_group_name === duplicatedTaskName ||
                                u.usecase_name === duplicatedTaskName
                        );
                        setProjectCreatedBar({
                            visible: true,
                            projectName: firstDuplicatedTask.group_name,
                            taskName: duplicatedTaskName,
                            usecaseId: newUsecase ? newUsecase._id : null,
                            isProjectCreation: false,
                        });
                        closeDuplicateTaskModal();
                    });
                } else {
                    sparkToast.error(result?.message || 'Error. Task with this name may already exist!');
                }
            }
        );
    }, [refreshUsecases, closeDuplicateTaskModal]);

    const handleEditTask = useCallback((task, mode = 'umbrella') => {
        setTaskToEdit(task);
        setEditTaskMode(mode === 'subtask' ? 'subtask' : 'umbrella');
        setIsEditTaskModalOpen(true);
    }, []);

    const closeEditTaskModal = useCallback(() => {
        setIsEditTaskModalOpen(false);
        setTaskToEdit(null);
        setEditTaskMode('umbrella');
    }, []);

    const handleUpdateTask = useCallback((updatedTaskData) => {
        if (!taskToEdit) return;

        data_from_server_post(
            'update_llm_usecase',
            {
                usecase_id: taskToEdit._id,
                client_id: taskToEdit.client_id,
                usecase_name: updatedTaskData.usecase_name,
                usecase_goal: updatedTaskData.usecase_goal,
                task_type: updatedTaskData.task_type,
                group_name: updatedTaskData.group_name,
                sub_group_name: updatedTaskData.sub_group_name,
                propagate_sub_group_name: !!updatedTaskData.propagate_sub_group_name,
                is_multilabel: updatedTaskData.is_multilabel || false,
                classify_speakers: updatedTaskData.classify_speakers || false,
                confidence_rejection_enabled:
                    updatedTaskData.confidence_rejection_enabled !== false,
                rejection_label_name: updatedTaskData.rejection_label_name ?? null,
                task_languages: updatedTaskData.task_languages || [],
                // Business goals fields — only sent when the modal included
                // them so partial edits don't clobber existing values.
                ...(updatedTaskData.business_goal_priorities !== undefined && {
                    business_goal_priorities: updatedTaskData.business_goal_priorities,
                }),
                ...(updatedTaskData.constraints !== undefined && {
                    constraints: updatedTaskData.constraints,
                }),
                ...(updatedTaskData.manually_added_labels !== undefined && {
                    manually_added_labels: updatedTaskData.manually_added_labels,
                }),
                ...(updatedTaskData.manually_added_entities !== undefined && {
                    manually_added_entities: updatedTaskData.manually_added_entities,
                }),
                // "What matters most for these labels?" — null lets the
                // backend keep (or auto-clear) the stored choice.
                ...(updatedTaskData.label_priority !== undefined && {
                    label_priority: updatedTaskData.label_priority,
                }),
            },
            (result) => {
                if (result.success) {
                    sparkToast.success('Task updated successfully!');
                    refreshUsecases();
                    closeEditTaskModal();
                    // Renaming the task changes the /task/:taskName URL segment.
                    const newTaskName = (
                        updatedTaskData.propagate_sub_group_name
                            ? updatedTaskData.sub_group_name
                            : updatedTaskData.sub_group_name || updatedTaskData.usecase_name
                    );
                    if (newTaskName) {
                        navigate(
                            `/task/${encodeURIComponent(newTaskName)}?task_id=${taskToEdit._id}`,
                            { replace: true }
                        );
                    }
                } else {
                    sparkToast.error(result.message || 'Error updating task!');
                }
            }
        );
    }, [taskToEdit, refreshUsecases, closeEditTaskModal, navigate]);

    const handleBulkMoveUsecases = useCallback(async (usecases = [], destGroupName) => {
        try {
            const { success, moved, failures } = await runBulkMove(
                usecases,
                destGroupName,
                allUsecases,
                data_from_server_post
            );
            if (failures[0] === 'No tasks selected to move') {
                return { success: false, message: failures[0] };
            }

            refreshUsecases();

            if (success) {
                sparkToast.success(
                    `Moved ${usecases.length} item${usecases.length === 1 ? '' : 's'} to ${destGroupName}`
                );
                return { success: true, moved_count: moved };
            }

            sparkToast.error(failures[0]);
            return { success: moved > 0, moved_count: moved, failed_count: failures.length };
        } catch (error) {
            sparkToast.error('Failed to move tasks');
            return { success: false, message: 'Failed to move tasks' };
        }
    }, [allUsecases, refreshUsecases]);

    return {
        // useTaskActions contract
        handleDeleteUsecase,
        handleBulkDeleteUsecases,
        handleBulkMoveUsecases,
        handleModelDeployment,
        handleDuplicateUsecase,
        handleEditTask,
        // Modal state + submit handlers (rendered by TaskPage)
        isDuplicateTaskModalOpen,
        taskToDuplicate,
        closeDuplicateTaskModal,
        handleCreateDuplicateTask,
        projectCreatedBar,
        closeProjectCreatedBar,
        isEditTaskModalOpen,
        taskToEdit,
        editTaskMode,
        closeEditTaskModal,
        handleUpdateTask,
    };
};

export default useTaskPageActions;
